import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CapacitorHttp, Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { URLS } from '../constants/URLS';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  private formHttpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
  };

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  // GET request
  get(url: string, options?: any): Observable<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativeGet(url, options);
    } else {
      return this.http.get(url, options);
    }
  }

  // POST request with JSON body
  post(url: string, body: any, options?: any): Observable<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativePost(url, body, options);
    } else {
      return this.http.post(url, body, options || this.httpOptions);
    }
  }

  // POST request with form data
  postForm(url: string, body: string, options?: any): Observable<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativePostForm(url, body, options);
    } else {
      return this.http.post(url, body, options || this.formHttpOptions);
    }
  }

  // PUT request
  put(url: string, body: any, options?: any): Observable<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativePut(url, body, options);
    } else {
      return this.http.put(url, body, options || this.httpOptions);
    }
  }

  // DELETE request
  delete(url: string, options?: any): Observable<any> {
    if (Capacitor.isNativePlatform()) {
      return this.nativeDelete(url, options);
    } else {
      return this.http.delete(url, options);
    }
  }

  // Native HTTP implementations
  private nativeGet(url: string, options?: any): Observable<any> {
    return from(this.makeNativeRequest('GET', url, null, options));
  }

  private nativePost(url: string, body: any, options?: any): Observable<any> {
    return from(this.makeNativeRequest('POST', url, body, options));
  }

  private nativePostForm(url: string, body: string, options?: any): Observable<any> {
    const formOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...options?.headers
      }
    };
    return from(this.makeNativeRequest('POST', url, body, formOptions));
  }

  private nativePut(url: string, body: any, options?: any): Observable<any> {
    return from(this.makeNativeRequest('PUT', url, body, options));
  }

  private nativeDelete(url: string, options?: any): Observable<any> {
    return from(this.makeNativeRequest('DELETE', url, null, options));
  }

  // Main method with token refresh logic (same as HttpConfigInterceptor)
  private async makeNativeRequest(method: string, url: string, body?: any, options?: any): Promise<any> {
    try {
      return await this.executeNativeRequest(method, url, body, options);
    } catch (error: any) {
      // Handle 401 - same logic as HttpConfigInterceptor
      if (error.status === 401) {
        console.log('Token expired, attempting refresh...');
        
        // Remove expired token (same as interceptor)
        localStorage.removeItem("token");
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken) {
          try {
            // Call refresh token API (same as interceptor)
            const refreshResponse = await this.refreshTokenNative(refreshToken);
            
            if (refreshResponse && refreshResponse.refreshToken) {
              // Store new token (same as interceptor)
              localStorage.setItem('token', refreshResponse.refreshToken);
              console.log('Token refreshed successfully, retrying request...');
              
              // Retry original request with new token (same as interceptor)
              return await this.executeNativeRequest(method, url, body, options);
            } else {
              throw new Error('Token refresh failed - no new token received');
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Handle refresh failure (same as interceptor)
            this.handleRefreshFailure();
            throw refreshError;
          }
        } else {
          console.error('No refresh token available');
          // Handle no refresh token (same as interceptor)
          this.handleNoRefreshToken();
          throw new Error('No refresh token available');
        }
      }
      // Handle other errors (same as interceptor)
      else if (error.status === 403) {
        this.handleBlock();
      } else if (error.status === 409) {
        this.handleConflict();
      } else if (error.status === 0) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  }

  // Execute the actual HTTP request
  private async executeNativeRequest(method: string, url: string, body?: any, options?: any): Promise<any> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...options?.headers
    };

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`🚀 Native ${method} request to:`, url);
    console.log('📋 Headers:', headers);
    console.log('📋 Body:', body);

    const response = await CapacitorHttp.request({
      url,
      method: method as any,
      headers,
      data: body
    });

    console.log(`✅ Native ${method} response:`, url, 'Status:', response.status);
    console.log('📊 Response:', response.data);

    // Parse response if it's a string
    let responseData = response.data;
    if (typeof response.data === 'string' && response.data.trim().startsWith('{')) {
      try {
        responseData = JSON.parse(response.data);
      } catch (parseError) {
        console.warn('Failed to parse response as JSON:', parseError);
      }
    }

    // Handle different response status codes
    if (response.status >= 200 && response.status < 300) {
      return responseData;
    } else {
      // Create error object with status
      const error = new Error(`HTTP ${response.status}: Request failed`);
      (error as any).status = response.status;
      (error as any).response = responseData;
      throw error;
    }
  }

  // Native token refresh (same logic as HttpConfigInterceptor)
  private async refreshTokenNative(refreshToken: string): Promise<any> {
    try {
      const requestBody = {
        "tqrIdToken": refreshToken
      };

      console.log('🔄 Refreshing token with:', requestBody);

      const response = await CapacitorHttp.request({
        url: URLS.userLogin, // Same URL as your interceptor uses
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        data: requestBody
      });

      console.log('🔄 Token refresh response:', response);

      if (response.status === 200) {
        const responseData = typeof response.data === 'string' ? 
          JSON.parse(response.data) : response.data;
        
        // Same validation as your interceptor
        if (responseData.responseCode === 200 || responseData.refreshToken) {
          return responseData;
        }
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('Native token refresh error:', error);
      throw error;
    }
  }

  // Error handlers (same as interceptor)
  private handleRefreshFailure(): void {
    localStorage.removeItem('refreshToken');
    this.logout();
  }

  private handleNoRefreshToken(): void {
    this.logout();
  }

  private handleBlock(): void {
    const token = localStorage.getItem('token');
    if (token) {
      this.logout();
    }
    // You might want to show error notification here
  }

  private handleConflict(): void {
    this.logout();
    // You might want to show warning notification here
  }

  private logout(): void {
    try {
      localStorage.clear();
      this.router.navigate(['login'], { replaceUrl: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation
      window.location.href = '/login';
    }
  }

  // Helper methods for common API patterns
  
  // For APIs that return {status, data} format
  postWithStatusCheck(url: string, body: any, options?: any): Observable<any> {
    return this.post(url, body, options).pipe(
      map(response => {
        if (response.status === '0' || response.status === 0) {
          return response.data;
        } else {
          throw new Error(response.message || 'API request failed');
        }
      })
    );
  }

  // For form data APIs that return {status, data} format
  postFormWithStatusCheck(url: string, body: string, options?: any): Observable<any> {
    return this.postForm(url, body, options).pipe(
      map(response => {
        if (response.status === '0' || response.status === 0) {
          return response.data;
        } else {
          throw new Error(response.message || 'API request failed');
        }
      })
    );
  }

  // For APIs that return {responseCode, responseDto} format
  postWithResponseCode(url: string, body: any, options?: any): Observable<any> {
    return this.post(url, body, options).pipe(
      map(response => {
        if (response.responseCode === 200 || response.reasonCode === 'OK') {
          return response.responseDto;
        } else {
          throw new Error(response.message || 'API request failed');
        }
      })
    );
  }
}